import { formatMoney, type Moneda } from '@/shared/billing/currency';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { AccessDenied, Button, Input, ModalShell } from '@/presentation/components/common';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { useToast } from '@/presentation/contexts/ToastContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';
import { formatPeriodLabel } from '@/presentation/components/billing/ClinicInvoiceDocument';

type ReceivedPayment = {
  id: number;
  tenantId: string | null;
  nombreClinica: string;
  fechaPago: string | null;
  monto: number;
  moneda: Moneda;
  medioPagoId: number | null;
  medioPago: string | null;
  origen: string | null;
  facturas: { id: number; periodo: string }[];
  comprobanteKey: string | null;
  notas: string | null;
  registradoPor: string | null;
  avisoId: number | null;
  anulado: boolean;
  anuladoAt: string | null;
  anuladoPor: string | null;
  motivoAnulacion: string | null;
};

type ListResponse = {
  desde: string;
  hasta: string;
  items: ReceivedPayment[];
  totales: { moneda: Moneda; monto: number; cantidad: number }[];
};

type RangePreset = 'thisMonth' | 'lastMonth' | 'last30' | 'custom';
const ORIGINS = ['manual', 'aviso', 'mercadopago'] as const;

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function presetRange(preset: Exclude<RangePreset, 'custom'>): { desde: string; hasta: string } {
  const now = new Date();
  if (preset === 'lastMonth') {
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 0);
    return { desde: ymd(first), hasta: ymd(last) };
  }
  if (preset === 'last30') {
    const from = new Date(now);
    from.setDate(from.getDate() - 29);
    return { desde: ymd(from), hasta: ymd(now) };
  }
  return { desde: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), hasta: ymd(now) };
}

const ORIGIN_BADGE: Record<string, string> = {
  manual: 'bg-slate-100 text-slate-700 dark:bg-gray-700 dark:text-slate-200',
  aviso: 'bg-sky-50 text-sky-800 dark:bg-sky-900/30 dark:text-sky-200',
  mercadopago: 'bg-indigo-50 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-200',
};

const csvCell = (value: string | number | null | undefined) => {
  const s = value == null ? '' : String(value);
  return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** Cobros reales (PanelPagos) por fecha de pago, con su origen y los anulados como historial. */
export const ReceivedPaymentsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { canAdminister, canOperateBilling } = useAuth();
  const { error: showError, success } = useToast();
  const canView = canAdminister || canOperateBilling;

  const [preset, setPreset] = useState<RangePreset>('thisMonth');
  const [range, setRange] = useState(() => presetRange('thisMonth'));
  const [data, setData] = useState<ListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState('all');
  const [originFilter, setOriginFilter] = useState('all');
  const [showCancelled, setShowCancelled] = useState(true);
  const [cancelTarget, setCancelTarget] = useState<ReceivedPayment | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    if (!range.desde || !range.hasta) return;
    setIsLoading(true);
    try {
      const res = await httpClient.get<ListResponse>('/panel-pagos/cobros', {
        params: { desde: range.desde, hasta: range.hasta },
      });
      setData(res.data);
    } catch (err) {
      setData(null);
      showError(err instanceof Error ? err.message : i18next.t('pages.payments.received.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [range.desde, range.hasta, showError]);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  const items = useMemo(() => data?.items ?? [], [data]);

  const methodOptions = useMemo(
    () =>
      [...new Set(items.map((p) => p.medioPago).filter((m): m is string => Boolean(m)))].sort((a, b) =>
        a.localeCompare(b)
      ),
    [items]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((p) => {
      if (!showCancelled && p.anulado) return false;
      if (methodFilter !== 'all' && (p.medioPago || '') !== methodFilter) return false;
      if (originFilter !== 'all' && (p.origen || 'manual') !== originFilter) return false;
      if (!q) return true;
      return (
        p.nombreClinica.toLowerCase().includes(q) ||
        (p.notas || '').toLowerCase().includes(q) ||
        p.facturas.some((f) => f.periodo.includes(q))
      );
    });
  }, [items, search, methodFilter, originFilter, showCancelled]);

  /** Totales de lo que se ve (sin anulados), por moneda. */
  const visibleTotals = useMemo(() => {
    const map = new Map<Moneda, { monto: number; cantidad: number }>();
    for (const p of filtered) {
      if (p.anulado) continue;
      const acc = map.get(p.moneda) ?? { monto: 0, cantidad: 0 };
      acc.monto += p.monto;
      acc.cantidad += 1;
      map.set(p.moneda, acc);
    }
    return [...map.entries()];
  }, [filtered]);

  const cancelledCount = useMemo(() => filtered.filter((p) => p.anulado).length, [filtered]);

  const formatDate = useCallback(
    (value: string | null) =>
      value
        ? new Date(value).toLocaleDateString(i18n.language, { day: '2-digit', month: '2-digit', year: 'numeric' })
        : '—',
    [i18n.language]
  );

  const originLabel = (origen: string | null) =>
    t(`pages.payments.received.origin.${origen || 'manual'}`, { defaultValue: origen || '—' });

  const applyPreset = (next: RangePreset) => {
    setPreset(next);
    if (next !== 'custom') setRange(presetRange(next));
  };

  const openReceipt = async (key: string) => {
    // Pestaña abierta antes del await para que no la bloquee el navegador.
    const win = window.open('', '_blank');
    if (win) win.opener = null;
    try {
      const res = await httpClient.get<{ url: string }>('/panel-pagos/comprobante-url', { params: { key } });
      if (win) win.location.href = res.data.url;
      else window.open(res.data.url, '_blank');
    } catch (err) {
      win?.close();
      showError(err instanceof Error ? err.message : i18next.t('pages.payments.received.receiptError'));
    }
  };

  const handleCancel = async () => {
    const facturaId = cancelTarget?.facturas[0]?.id;
    if (!cancelTarget || !facturaId || cancelling) return;
    setCancelling(true);
    try {
      await httpClient.post(`/panel-pagos/${facturaId}/cancel`, {
        motivo: cancelReason.trim() || undefined,
      });
      success(i18next.t('pages.billing.cancelPaymentSuccess'));
      setCancelTarget(null);
      setCancelReason('');
      await load();
    } catch (err) {
      showError(err instanceof Error ? err.message : i18next.t('common.error'));
    } finally {
      setCancelling(false);
    }
  };

  const exportCsv = () => {
    const header = [
      t('pages.payments.received.colDate'),
      t('pages.payments.received.colClinic'),
      t('pages.payments.received.colInvoices'),
      t('pages.payments.received.colAmount'),
      t('pages.payments.received.colCurrency'),
      t('pages.payments.received.colMethod'),
      t('pages.payments.received.colOrigin'),
      t('pages.payments.received.colRegisteredBy'),
      t('pages.payments.received.colStatus'),
      t('pages.payments.received.colNotes'),
    ];
    const lines = filtered.map((p) =>
      [
        p.fechaPago ? p.fechaPago.slice(0, 10) : '',
        p.nombreClinica,
        p.facturas.map((f) => f.periodo).join(' '),
        p.monto.toFixed(2),
        p.moneda,
        p.medioPago || '',
        originLabel(p.origen),
        p.registradoPor || '',
        p.anulado ? t('pages.payments.received.cancelled') : t('pages.payments.received.valid'),
        p.anulado ? p.motivoAnulacion || '' : p.notas || '',
      ]
        .map(csvCell)
        .join(';')
    );
    const blob = new Blob([`﻿${[header.map(csvCell).join(';'), ...lines].join('\n')}`], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${t('pages.payments.received.fileName')}-${range.desde}_${range.hasta}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!canView) return <AccessDenied />;

  const selectClass =
    'h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white';

  return (
    <div className="panel-page">
      <div className="panel-page-header">
        <div className="min-w-0">
          <h1 className="panel-page-title">{t('pages.payments.received.title')}</h1>
          <p className="panel-page-subtitle hidden sm:block">{t('pages.payments.received.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => void load()} disabled={isLoading}>
            {t('common.refresh')}
          </Button>
          <Button size="sm" onClick={exportCsv} disabled={isLoading || filtered.length === 0}>
            {t('pages.payments.received.export')}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('pages.payments.received.range')}
          </span>
          <select value={preset} onChange={(e) => applyPreset(e.target.value as RangePreset)} className={selectClass}>
            <option value="thisMonth">{t('pages.payments.received.presets.thisMonth')}</option>
            <option value="lastMonth">{t('pages.payments.received.presets.lastMonth')}</option>
            <option value="last30">{t('pages.payments.received.presets.last30')}</option>
            <option value="custom">{t('pages.payments.received.presets.custom')}</option>
          </select>
        </label>
        <Input
          type="date"
          label={t('pages.payments.received.from')}
          value={range.desde}
          max={range.hasta}
          onChange={(e) => {
            setPreset('custom');
            setRange((r) => ({ ...r, desde: e.target.value }));
          }}
        />
        <Input
          type="date"
          label={t('pages.payments.received.to')}
          value={range.hasta}
          min={range.desde}
          onChange={(e) => {
            setPreset('custom');
            setRange((r) => ({ ...r, hasta: e.target.value }));
          }}
        />
        <Input
          label={t('common.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('pages.payments.received.searchPlaceholder')}
        />
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('pages.payments.received.colMethod')}
          </span>
          <select value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)} className={selectClass}>
            <option value="all">{t('pages.payments.received.allMethods')}</option>
            {methodOptions.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('pages.payments.received.colOrigin')}
          </span>
          <select value={originFilter} onChange={(e) => setOriginFilter(e.target.value)} className={selectClass}>
            <option value="all">{t('pages.payments.received.allOrigins')}</option>
            {ORIGINS.map((o) => (
              <option key={o} value={o}>
                {originLabel(o)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-end gap-2 pb-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            checked={showCancelled}
            onChange={(e) => setShowCancelled(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
          />
          {t('pages.payments.received.showCancelled')}
        </label>
      </div>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-3">
        {visibleTotals.length === 0 ? (
          <div className="panel-stat !p-3 border-l-4 border-green-500">
            <p className="panel-stat-label">{t('pages.payments.received.totalCollected')}</p>
            <p className="panel-stat-value !mt-0.5 text-lg md:text-2xl">{formatMoney(0, 'ARS', { decimals: false })}</p>
          </div>
        ) : (
          visibleTotals.map(([moneda, total]) => (
            <div key={moneda} className="panel-stat !p-3 border-l-4 border-green-500">
              <p className="panel-stat-label">
                {t('pages.payments.received.totalCollected')} · {moneda}
              </p>
              <p className="panel-stat-value !mt-0.5 text-lg text-green-600 dark:text-green-400 md:text-2xl">
                {formatMoney(total.monto, moneda, { decimals: false })}
              </p>
              <p className="text-[11px] text-slate-500">
                {t('pages.payments.received.paymentsCount', { count: total.cantidad })}
              </p>
            </div>
          ))
        )}
        <div className="panel-stat !p-3 border-l-4 border-rose-400">
          <p className="panel-stat-label">{t('pages.payments.received.cancelledTitle')}</p>
          <p className="panel-stat-value !mt-0.5 text-lg md:text-2xl">{cancelledCount}</p>
        </div>
      </div>

      <div className="panel-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:border-gray-700 dark:bg-gray-900/40 dark:text-slate-400">
                <th className="px-4 py-3">{t('pages.payments.received.colDate')}</th>
                <th className="px-4 py-3">{t('pages.payments.received.colClinic')}</th>
                <th className="px-4 py-3">{t('pages.payments.received.colInvoices')}</th>
                <th className="px-4 py-3 text-right">{t('pages.payments.received.colAmount')}</th>
                <th className="px-4 py-3">{t('pages.payments.received.colMethod')}</th>
                <th className="px-4 py-3">{t('pages.payments.received.colOrigin')}</th>
                <th className="px-4 py-3 text-right">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    {t('common.loading')}
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    {t('pages.payments.received.empty')}
                  </td>
                </tr>
              ) : (
                filtered.map((p) => (
                  <tr
                    key={p.id}
                    className={`hover:bg-slate-50/80 dark:hover:bg-gray-900/30 ${p.anulado ? 'opacity-60' : ''}`}
                  >
                    <td className="whitespace-nowrap px-4 py-3 tabular-nums">{formatDate(p.fechaPago)}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900 dark:text-white">{p.nombreClinica}</p>
                      {p.registradoPor ? (
                        <p className="text-xs text-slate-500">
                          {t('pages.payments.received.registeredBy', { user: p.registradoPor })}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      {p.facturas.length === 0
                        ? '—'
                        : p.facturas.map((f, i) => (
                            <React.Fragment key={f.id}>
                              {i > 0 ? ', ' : null}
                              <Link
                                to={`/finanzas/facturas?periodo=${f.periodo}&q=${encodeURIComponent(p.nombreClinica)}`}
                                className="text-teal-700 hover:underline dark:text-teal-300"
                              >
                                {formatPeriodLabel(f.periodo, i18n.language)}
                              </Link>
                            </React.Fragment>
                          ))}
                    </td>
                    <td
                      className={`whitespace-nowrap px-4 py-3 text-right font-semibold tabular-nums ${
                        p.anulado ? 'line-through' : ''
                      }`}
                    >
                      {formatMoney(p.monto, p.moneda, { decimals: false })}
                    </td>
                    <td className="px-4 py-3">{p.medioPago || '—'}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          ORIGIN_BADGE[p.origen || 'manual'] || ORIGIN_BADGE.manual
                        }`}
                      >
                        {originLabel(p.origen)}
                      </span>
                      {p.anulado ? (
                        <span
                          className="ml-1 inline-flex rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-800 dark:bg-rose-900/30 dark:text-rose-200"
                          title={p.motivoAnulacion || undefined}
                        >
                          {t('pages.payments.received.cancelled')}
                        </span>
                      ) : null}
                      {p.anulado ? (
                        <p className="mt-0.5 text-[11px] text-slate-500">
                          {t('pages.payments.received.cancelledInfo', {
                            date: formatDate(p.anuladoAt),
                            user: p.anuladoPor || '—',
                          })}
                          {p.motivoAnulacion ? ` · ${p.motivoAnulacion}` : ''}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {p.comprobanteKey ? (
                          <Button size="sm" variant="secondary" onClick={() => void openReceipt(p.comprobanteKey!)}>
                            {t('pages.payments.received.viewReceipt')}
                          </Button>
                        ) : null}
                        {!p.anulado && p.facturas.length > 0 && canOperateBilling ? (
                          <Button size="sm" variant="ghost" onClick={() => setCancelTarget(p)}>
                            {t('pages.clinicInvoices.cancelPaymentAction')}
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ModalShell
        isOpen={!!cancelTarget}
        onClose={() => (cancelling ? undefined : setCancelTarget(null))}
        title={t('pages.clinicInvoices.cancelPaymentAction')}
        size="md"
        footer={
          <>
            <Button
              variant="danger"
              onClick={() => void handleCancel()}
              disabled={cancelling}
              isLoading={cancelling}
              className="w-full sm:w-auto"
            >
              {t('pages.clinicInvoices.cancelPaymentAction')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setCancelTarget(null)}
              disabled={cancelling}
              className="w-full sm:w-auto"
            >
              {t('common.cancel')}
            </Button>
          </>
        }
      >
        {cancelTarget ? (
          <div className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
            <p>
              <span className="font-semibold text-slate-900 dark:text-white">{cancelTarget.nombreClinica}</span>
              {' · '}
              {formatMoney(cancelTarget.monto, cancelTarget.moneda, { decimals: false })}
              {' · '}
              {cancelTarget.facturas.map((f) => formatPeriodLabel(f.periodo, i18n.language)).join(', ')}
            </p>
            <p>{t('pages.clinicInvoices.cancelPaymentHint')}</p>
            <label className="block">
              <span className="mb-1 block font-medium">{t('pages.payments.received.cancelReason')}</span>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder={t('pages.payments.received.cancelReasonPlaceholder')}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </label>
          </div>
        ) : null}
      </ModalShell>
    </div>
  );
};
